import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import { 
  useSuppliersData, 
  usePackagesData, 
  useFinancialData, 
  useReservationsData,
  useClientsData,
  useDashboardData 
} from '../../services/dataService';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Menu,
  MenuList,
  MenuItem as MenuListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Rating,
  LinearProgress,
  Paper,
  List,
  ListItem,
  ListItemText as MuiListItemText,
  ListItemAvatar,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Badge,
  Skeleton
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Flight as FlightIcon,
  Hotel as HotelIcon,
  DirectionsBus as BusIcon,
  LocalTaxi as TaxiIcon,
  Restaurant as RestaurantIcon,
  Security as InsuranceIcon,
  Tour as TourIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  GetApp as ExportIcon,
  FilterList as FilterIcon,
  Handshake as HandshakeIcon,
  Assessment as AssessmentIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Print as PrintIcon,
  PersonAdd as PersonAddIcon,
  Assignment as AssignmentIcon,
  Insights as InsightsIcon,
  CardTravel as CardTravelIcon,
  AccountBalance as AccountBalanceIcon,
  Timeline as TimelineIcon,
  Speed as SpeedIcon,
  Public as PublicIcon,
  Refresh as RefreshIcon,
  PictureAsPdf as PictureAsPdfIcon
} from '@mui/icons-material';

import SupplierDetails from '../../components/suppliers/SupplierDetails';

// Enhanced charts components
const SupplierPerformanceChart = ({ data }) => (
  <Box sx={{ height: 200, bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
    <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
      Performance des Fournisseurs (Score sur 100)
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'end', height: '70%', gap: 1 }}>
      {data.map((supplier, index) => (
        <Tooltip key={index} title={`${supplier.name}: ${supplier.score}%`}>
          <Box
            sx={{
              flex: 1,
              height: `${supplier.score}%`,
              bgcolor: supplier.score > 80 ? '#4caf50' : supplier.score > 60 ? '#ff9800' : '#f44336',
              borderRadius: '4px 4px 0 0',
              minHeight: '10px',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': { opacity: 0.8, transform: 'scaleY(1.05)' }
            }}
          />
        </Tooltip>
      ))}
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
      {data.map((supplier, index) => (
        <Typography key={index} variant="caption" color="text.secondary" sx={{ transform: 'rotate(-45deg)', fontSize: '0.65rem' }}>
          {supplier.name}
        </Typography>
      ))}
    </Box>
  </Box>
);

const SupplierDistributionChart = ({ data }) => (
  <Box sx={{ height: 200, bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
    <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
      Distribution par Type de Service
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60%' }}>
      <Box sx={{ position: 'relative', width: 120, height: 120 }}>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            background: `conic-gradient(
              #1976d2 0deg ${data.compagnies * 3.6}deg,
              #2e7d32 ${data.compagnies * 3.6}deg ${(data.compagnies + data.hotels) * 3.6}deg,
              #ed6c02 ${(data.compagnies + data.hotels) * 3.6}deg ${(data.compagnies + data.hotels + data.transport) * 3.6}deg,
              #9c27b0 ${(data.compagnies + data.hotels + data.transport) * 3.6}deg 360deg
            )`
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '60%',
            height: '60%',
            bgcolor: 'white',
            borderRadius: '50%'
          }}
        />
      </Box>
    </Box>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 1 }}>
      <Chip label={`Aérien ${data.compagnies}%`} size="small" sx={{ bgcolor: '#1976d2', color: 'white' }} />
      <Chip label={`Hôtels ${data.hotels}%`} size="small" sx={{ bgcolor: '#2e7d32', color: 'white' }} />
      <Chip label={`Transport ${data.transport}%`} size="small" sx={{ bgcolor: '#ed6c02', color: 'white' }} />
      <Chip label={`Autres ${data.autres}%`} size="small" sx={{ bgcolor: '#9c27b0', color: 'white' }} />
    </Box>
  </Box>
);

const Fournisseurs = () => {
  // Settings context for real-time data
  const { formatCurrency, settings, formatDate } = useSettings();
  
  // Use data services for real-time interconnected data
  const { suppliers, loading: suppliersLoading, error: suppliersError, stats: suppliersStats } = useSuppliersData();
  const { packages, loading: packagesLoading } = usePackagesData();
  const { transactions, loading: transactionsLoading } = useFinancialData();
  const { reservations, loading: reservationsLoading } = useReservationsData();
  const { clients, loading: clientsLoading } = useClientsData();
  const { dashboardData, loading: dashboardLoading } = useDashboardData();
  
  // Combined loading state
  const isLoading = suppliersLoading || packagesLoading || transactionsLoading || 
                    reservationsLoading || clientsLoading || dashboardLoading;

  // All useMemo hooks need to be at the top level, before any conditional logic
  // Get interconnected packages with supplier data
  const interconnectedPackages = useMemo(() => {
    const result = [];
    if (packages && suppliers && packages.length > 0 && suppliers.length > 0) {
      return packages.filter(pkg => {
        // Check if package has a supplier reference
        return pkg.fournisseurId && suppliers.some(s => s.id === pkg.fournisseurId);
      });
    }
    return result;
  }, [packages, suppliers]);
  
  // Get interconnected transactions with supplier data
  const interconnectedTransactions = useMemo(() => {
    const result = [];
    if (transactions && suppliers && transactions.length > 0 && suppliers.length > 0) {
      return transactions.filter(transaction => {
        // Check if transaction is an expense and has a supplier reference
        return transaction.type === 'Dépense' && 
               transaction.categorie === 'Fournisseur' && 
               transaction.fournisseurId && 
               suppliers.some(s => s.id === transaction.fournisseurId);
      });
    }
    return result;
  }, [transactions, suppliers]);
  
  // Get reservations connected to suppliers through packages
  const interconnectedReservations = useMemo(() => {
    const result = [];
    if (reservations && packages && suppliers) {
      return reservations.filter(reservation => {
        // Find the package for this reservation
        const pkg = packages.find(p => p.id === reservation.forfaitId);
        // Check if package exists and has a supplier reference
        return pkg && pkg.fournisseurId && suppliers.some(s => s.id === pkg.fournisseurId);
      });
    }
    return result;
  }, [reservations, packages, suppliers]);
  
  // Calculate supplier performance metrics
  const supplierMetrics = useMemo(() => {
    if (!suppliers || !transactions || !reservations || !packages) {
      return { topPerformers: [], lowPerformers: [], revenueBySupplier: {} };
    }
    
    // Calculate revenue by supplier
    const revenueBySupplier = suppliers.reduce((acc, supplier) => {
      // Find all transactions related to this supplier
      const supplierTransactions = transactions.filter(t => 
        t.fournisseurId === supplier.id
      );
      
      // Calculate total revenue
      const totalRevenue = supplierTransactions.reduce((sum, t) => 
        sum + (t.montant || 0), 0
      );
      
      acc[supplier.id] = totalRevenue;
      return acc;
    }, {});
    
    // Sort suppliers by performance score
    const sortedSuppliers = [...(suppliers || [])].sort((a, b) => 
      ((b.performanceScore || 0) - (a.performanceScore || 0))
    );
    
    return {
      topPerformers: sortedSuppliers.slice(0, 3),
      lowPerformers: sortedSuppliers.slice(-3).reverse(),
      revenueBySupplier
    };
  }, [suppliers, transactions, reservations, packages]);
  
  // Calculate type distribution based on actual supplier data
  const typeDistribution = useMemo(() => {
    const defaultDistribution = { compagnies: 25, hotels: 25, transport: 25, autres: 25 };
    
    if (!suppliers || suppliers.length === 0) {
      return defaultDistribution;
    }
    
    // Count suppliers by type
    const typeCounts = suppliers.reduce((acc, supplier) => {
      const type = supplier.type?.toLowerCase() || 'autres';
      
      if (type.includes('aérien') || type.includes('compagnie')) {
        acc.compagnies = (acc.compagnies || 0) + 1;
      } else if (type.includes('hôtel') || type.includes('hotel') || type.includes('hébergement')) {
        acc.hotels = (acc.hotels || 0) + 1;
      } else if (type.includes('transport') || type.includes('taxi') || type.includes('bus')) {
        acc.transport = (acc.transport || 0) + 1;
      } else {
        acc.autres = (acc.autres || 0) + 1;
      }
      
      return acc;
    }, { compagnies: 0, hotels: 0, transport: 0, autres: 0 });
    
    // Calculate percentages
    const total = Object.values(typeCounts).reduce((sum, count) => sum + count, 0) || 1;
    
    return {
      compagnies: Math.round((typeCounts.compagnies / total) * 100),
      hotels: Math.round((typeCounts.hotels / total) * 100),
      transport: Math.round((typeCounts.transport / total) * 100),
      autres: Math.round((typeCounts.autres / total) * 100)
    };
  }, [suppliers]);
  
  // Calculate total revenue from transactions related to suppliers
  const totalChiffreAffaires = useMemo(() => {
    let total = 0;
    
    if (transactions && transactions.length > 0) {
      total = transactions
        .filter(t => t.type === 'Dépense' && t.categorie === 'Fournisseur')
        .reduce((sum, t) => sum + (t.montant || 0), 0);
    }
    
    return total;
  }, [transactions]);
  
  // Calculate average performance score
  const performanceMoyenne = useMemo(() => {
    let average = 0;
    
    if (suppliers && suppliers.length > 0) {
      const totalScore = suppliers.reduce((sum, s) => sum + (s.performanceScore || 0), 0);
      average = totalScore / suppliers.length;
    }
    
    return average;
  }, [suppliers]);
  
  // Calculate average rating
  const noteMoyenne = useMemo(() => {
    let average = 0;
    
    if (suppliers && suppliers.length > 0) {
      const totalRating = suppliers.reduce((sum, s) => sum + (s.rating || 0), 0);
      average = totalRating / suppliers.length;
    }
    
    return average;
  }, [suppliers]);
  
  // Count unique clients who have booked with these suppliers
  const totalClients = useMemo(() => {
    let count = 0;
    
    if (reservations && packages && suppliers) {
      // Get reservations connected to suppliers
      const supplierReservations = reservations.filter(res => {
        const pkg = packages.find(p => p.id === res.forfaitId);
        return pkg && pkg.fournisseurId && suppliers.some(s => s.id === pkg.fournisseurId);
      });
      
      // Count unique clients
      const uniqueClientIds = new Set(supplierReservations.map(res => res.clientId));
      count = uniqueClientIds.size;
    }
    
    return count;
  }, [reservations, packages, suppliers]);
  
  // Get unique countries and types for filters
  const uniqueCountries = useMemo(() => {
    if (!suppliers) return [];
    return [...new Set(suppliers.map(f => f.pays).filter(Boolean) || [])];
  }, [suppliers]);
  
  const uniqueTypes = useMemo(() => {
    if (!suppliers) return [];
    return [...new Set(suppliers.map(f => f.type).filter(Boolean) || [])];
  }, [suppliers]);
  
  // Chart data from real-time suppliers
  const performanceChartData = useMemo(() => {
    if (!suppliers || suppliers.length === 0) {
      return [];
    }
    
    return suppliers.slice(0, 6).map(f => ({
      name: f.nom?.substring(0, 8) || '',
      score: f.performanceScore || Math.floor(Math.random() * 30) + 70 // Fallback if no score
    }));
  }, [suppliers]);
  
  // Enhanced statistics with interconnected data from real-time services
  const totalFournisseurs = suppliers?.length || 0;
  const fournisseursActifs = suppliers?.filter(s => s.statut === 'Actif')?.length || 0;
  
  const statsCards = [
    {
      title: 'Total Fournisseurs',
      value: totalFournisseurs,
      icon: BusinessIcon,
      color: 'primary',
      trend: '+12%',
      subtitle: `${fournisseursActifs} actifs`
    },
    {
      title: 'Chiffre d\'Affaires',
      value: totalChiffreAffaires,
      icon: MoneyIcon,
      color: 'success',
      trend: '+25%',
      isCurrency: true,
      subtitle: 'Revenue total généré'
    },
    {
      title: 'Performance Moyenne',
      value: `${performanceMoyenne.toFixed(1)}%`,
      icon: SpeedIcon,
      color: 'info',
      trend: '+3.2%',
      subtitle: 'Score de performance'
    },
    {
      title: 'Satisfaction Client',
      value: noteMoyenne.toFixed(1),
      icon: StarIcon,
      color: 'warning',
      trend: '+0.3',
      subtitle: `Sur ${totalClients} clients`
    }
  ];
  
  const [filteredFournisseurs, setFilteredFournisseurs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [viewMode, setViewMode] = useState('cards');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFournisseur, setEditingFournisseur] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFournisseur, setSelectedFournisseur] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [formData, setFormData] = useState({
    nom: '',
    type: 'Hôtel',
    pays: '',
    ville: '',
    email: '',
    telephone: '',
    adresse: '',
    personneContact: '',
    statut: 'Actif',
    commission: 0,
    delaiPaiement: 30,
    description: ''
  });

  // DEFINE handleOpenDetails EARLY using useCallback to avoid reference errors
  const handleOpenDetails = useCallback((fournisseur) => {
    if (!fournisseur) return;
    
    try {
      // Create a clean supplier object with only the data that exists
      // and was entered during creation
      const enhancedSupplier = { ...fournisseur };
      
      // Get the latest data from localStorage for all related entities
      const latestPackages = JSON.parse(localStorage.getItem('packagesData') || '[]');
      const latestReservations = JSON.parse(localStorage.getItem('reservationsData') || '[]');
      const latestTransactions = JSON.parse(localStorage.getItem('transactionsData') || '[]');
      const latestClients = JSON.parse(localStorage.getItem('clientsData') || '[]');
      
      // Find related packages if they exist
      if (latestPackages && latestPackages.length > 0) {
        const relatedPackages = latestPackages.filter(pkg => pkg.fournisseurId === fournisseur.id);
        if (relatedPackages.length > 0) {
          enhancedSupplier.forfaitsGeres = relatedPackages.map(pkg => ({
            id: pkg.id,
            nom: pkg.nom,
            prix: pkg.prix,
            categorie: pkg.categorie,
            destination: pkg.destination,
            statut: pkg.statut
          }));
          
          // Find reservations related to these packages
          if (latestReservations && latestReservations.length > 0) {
            const packageIds = relatedPackages.map(pkg => pkg.id);
            const relatedReservations = latestReservations.filter(res => 
              packageIds.includes(res.forfaitId)
            );
            
            // Add reservation count to each package
            enhancedSupplier.forfaitsGeres = enhancedSupplier.forfaitsGeres.map(pkg => {
              const reservationCount = relatedReservations.filter(res => res.forfaitId === pkg.id).length;
              return {
                ...pkg,
                reservationCount
              };
            });
          }
        }
      }
      
      // Set the supplier object without adding any auto-generated data
      setSelectedFournisseur(enhancedSupplier);
      setDetailsOpen(true);
      setAnchorEl(null);
    } catch (error) {
      console.error('Error preparing supplier details:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors du chargement des détails du fournisseur',
        severity: 'error'
      });
    }
  }, []);

  const handleCloseDetails = useCallback(() => {
    setDetailsOpen(false);
  }, []);

  // Filter suppliers based on search term and filters
  useEffect(() => {
    if (!suppliersLoading && suppliers) {
      console.log('Filtering suppliers:', suppliers.length);
      let filtered = [...suppliers];
      
      // Apply search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        filtered = filtered.filter(f => 
          (f.nom && f.nom.toLowerCase().includes(search)) || 
          (f.type && f.type.toLowerCase().includes(search)) ||
          (f.pays && f.pays.toLowerCase().includes(search)) ||
          (f.ville && f.ville.toLowerCase().includes(search)) ||
          (f.personneContact && f.personneContact.toLowerCase().includes(search))
        );
      }
      
      // Apply type filter
      if (filterType !== 'all') {
        filtered = filtered.filter(f => f.type === filterType);
      }
      
      // Apply status filter
      if (filterStatus !== 'all') {
        filtered = filtered.filter(f => f.statut === filterStatus);
      }
      
      // Apply location filter
      if (filterLocation !== 'all') {
        filtered = filtered.filter(f => f.pays === filterLocation);
      }
    
      // CRITICAL: Only update filtered suppliers if we're not in the middle of an edit
      // Check if we have unsaved changes that might be lost
      const isEditing = openDialog && editingFournisseur;
      
      if (!isEditing) {
      setFilteredFournisseurs(filtered);
      } else {
        console.log('Skipping filter update while editing to prevent data loss');
      }
    }
  }, [
    filterType, filterStatus, filterLocation, searchTerm, 
    suppliersLoading, openDialog, editingFournisseur
  ]);

  // Function to update suppliers data from localStorage with current filters
  const handleSupplierDataUpdate = () => {
    try {
      // Load the latest data directly from localStorage
      const updatedSuppliers = JSON.parse(localStorage.getItem('suppliersData') || '[]');
      
      // First update the filtered suppliers list directly to ensure UI consistency
      if (Array.isArray(updatedSuppliers)) {
        console.log('Updating suppliers data with latest from localStorage');
        
        // Apply current filters to the updated suppliers
        let filtered = [...updatedSuppliers];
        
        // Apply search filter
        if (searchTerm) {
          const search = searchTerm.toLowerCase();
          filtered = filtered.filter(f => 
            (f.nom && f.nom.toLowerCase().includes(search)) || 
            (f.type && f.type.toLowerCase().includes(search)) ||
            (f.pays && f.pays.toLowerCase().includes(search)) ||
            (f.ville && f.ville.toLowerCase().includes(search)) ||
            (f.personneContact && f.personneContact.toLowerCase().includes(search))
          );
        }
        
        // Apply type filter
        if (filterType !== 'all') {
          filtered = filtered.filter(f => f.type === filterType);
        }
        
        // Apply status filter
        if (filterStatus !== 'all') {
          filtered = filtered.filter(f => f.statut === filterStatus);
        }
        
        // Apply location filter
        if (filterLocation !== 'all') {
          filtered = filtered.filter(f => f.pays === filterLocation);
        }
      
        // Update the state directly
        setFilteredFournisseurs(filtered);
      }
      
      // Show notification
      setSnackbar({
        open: true,
        message: 'Données des fournisseurs mises à jour en temps réel',
        severity: 'info'
      });
    } catch (error) {
      console.error('Error updating suppliers data:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la mise à jour des données',
        severity: 'error'
      });
    }
  };

  // Function to force refresh all data
  const refreshAllData = useCallback(() => {
    // Clear cache to force reload
    localStorage.removeItem('dataCache');
    
    try {
    // Reload data from localStorage
    const updatedSuppliers = JSON.parse(localStorage.getItem('suppliersData') || '[]');
      
      // Apply current filters to get filtered suppliers
      let filtered = [...updatedSuppliers];
      
      // Apply search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        filtered = filtered.filter(f => 
          (f.nom && f.nom.toLowerCase().includes(search)) || 
          (f.type && f.type.toLowerCase().includes(search)) ||
          (f.pays && f.pays.toLowerCase().includes(search)) ||
          (f.ville && f.ville.toLowerCase().includes(search)) ||
          (f.personneContact && f.personneContact.toLowerCase().includes(search))
        );
      }
      
      // Apply type filter
      if (filterType !== 'all') {
        filtered = filtered.filter(f => f.type === filterType);
      }
      
      // Apply status filter
      if (filterStatus !== 'all') {
        filtered = filtered.filter(f => f.statut === filterStatus);
      }
      
      // Apply location filter
      if (filterLocation !== 'all') {
        filtered = filtered.filter(f => f.pays === filterLocation);
      }
      
      // Update the state directly to ensure immediate UI update
      setFilteredFournisseurs(filtered);
    
    // Dispatch events to notify other components
    window.dispatchEvent(new CustomEvent('supplierDataUpdate'));
    window.dispatchEvent(new CustomEvent('packageDataUpdate'));
    window.dispatchEvent(new CustomEvent('financialDataUpdate'));
    window.dispatchEvent(new CustomEvent('reservationDataUpdate'));
    window.dispatchEvent(new CustomEvent('clientDataUpdate'));
    
    setSnackbar({
      open: true,
      message: 'Toutes les données ont été actualisées',
      severity: 'success'
    });
    } catch (error) {
      console.error('Error refreshing data:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de l\'actualisation des données',
        severity: 'error'
      });
    }
  }, [searchTerm, filterType, filterStatus, filterLocation]);

  // Add event listeners for real-time data updates from other pages
  useEffect(() => {
    // Function to handle storage events (localStorage changes)
    const handleStorageChange = (e) => {
      if (e.key === 'suppliersData') {
        // Don't rely on complex event handling - use a direct approach
        try {
        const updatedSuppliers = JSON.parse(localStorage.getItem('suppliersData') || '[]');
        
          // Update our global reference
          if (typeof window.__travelIT === 'undefined') {
            window.__travelIT = {};
          }
          window.__travelIT.suppliers = updatedSuppliers;
          
          // Instead of complex filtering, just force a re-render with new data
          // When we force the update, we must also apply the current filters
          let filtered = [...updatedSuppliers];
          
          // Apply all filters again
          if (searchTerm) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(f => 
              (f.nom && f.nom.toLowerCase().includes(search)) || 
              (f.type && f.type.toLowerCase().includes(search)) ||
              (f.pays && f.pays.toLowerCase().includes(search)) ||
              (f.ville && f.ville.toLowerCase().includes(search)) ||
              (f.personneContact && f.personneContact.toLowerCase().includes(search))
            );
          }
          
          if (filterType !== 'all') {
            filtered = filtered.filter(f => f.type === filterType);
          }
          
          if (filterStatus !== 'all') {
            filtered = filtered.filter(f => f.statut === filterStatus);
          }
          
          if (filterLocation !== 'all') {
            filtered = filtered.filter(f => f.pays === filterLocation);
          }
          
          // Update the filtered list directly
          setFilteredFournisseurs(filtered);
          
          // Show notification only if we didn't initiate this change
          if (!window.__travelIT.suppressNextNotification) {
        setSnackbar({
          open: true,
              message: 'Données des fournisseurs mises à jour en temps réel',
          severity: 'info'
        });
          } else {
            window.__travelIT.suppressNextNotification = false;
          }
        } catch (error) {
          console.error('Error processing supplier data update:', error);
        }
      }
    };

    // Function to handle custom events from other components
    const handleSupplierEventUpdate = (e) => {
      // Check if we have a complete payload with all suppliers
      if (e.detail && e.detail.allSuppliers && Array.isArray(e.detail.allSuppliers)) {
        // Use the full suppliers list provided in the event
        const updatedSuppliers = e.detail.allSuppliers;
        
        // Apply all filters to get the filtered list
        let filtered = [...updatedSuppliers];
        
        // Apply search filter
        if (searchTerm) {
          const search = searchTerm.toLowerCase();
          filtered = filtered.filter(f => 
            (f.nom && f.nom.toLowerCase().includes(search)) || 
            (f.type && f.type.toLowerCase().includes(search)) ||
            (f.pays && f.pays.toLowerCase().includes(search)) ||
            (f.ville && f.ville.toLowerCase().includes(search)) ||
            (f.personneContact && f.personneContact.toLowerCase().includes(search))
          );
        }
        
        // Apply type filter
        if (filterType !== 'all') {
          filtered = filtered.filter(f => f.type === filterType);
        }
        
        // Apply status filter
        if (filterStatus !== 'all') {
          filtered = filtered.filter(f => f.statut === filterStatus);
        }
        
        // Apply location filter
        if (filterLocation !== 'all') {
          filtered = filtered.filter(f => f.pays === filterLocation);
        }
        
        // Update the filtered list directly
        setFilteredFournisseurs(filtered);
      }
      // If we have just a single supplier update
      else if (e.detail && e.detail.action && e.detail.supplier) {
        const { action, supplier } = e.detail;
        
        if (action === 'update') {
          // Update the supplier in the filtered list
          setFilteredFournisseurs(prevFiltered => {
            return prevFiltered.map(item => 
              item.id === supplier.id ? supplier : item
            );
          });
        }
        else if (action === 'create') {
          // Check if the new supplier matches our filters
          const shouldAdd = 
            (filterType === 'all' || supplier.type === filterType) &&
            (filterStatus === 'all' || supplier.statut === filterStatus) &&
            (filterLocation === 'all' || supplier.pays === filterLocation);
          
          if (shouldAdd) {
            setFilteredFournisseurs(prev => [...prev, supplier]);
          }
        }
        else if (action === 'delete' && e.detail.supplierId) {
          // Remove the supplier from the filtered list
          setFilteredFournisseurs(prevFiltered => 
            prevFiltered.filter(s => s.id !== e.detail.supplierId)
          );
        }
      }
      
      // If a supplier details dialog is open, refresh its data
      if (detailsOpen && selectedFournisseur) {
        try {
        const updatedSuppliers = JSON.parse(localStorage.getItem('suppliersData') || '[]');
        const updatedSupplier = updatedSuppliers.find(s => s.id === selectedFournisseur.id);
        if (updatedSupplier) {
          handleOpenDetails(updatedSupplier);
          }
        } catch (error) {
          console.error('Error updating supplier details:', error);
        }
      }
    };

    // Other event handlers (keep existing ones)
    const handlePackageDataUpdate = (e) => {
      setSnackbar({
        open: true,
        message: 'Forfaits mis à jour - Relations fournisseurs actualisées',
        severity: 'info'
      });
      
      // If a supplier details dialog is open, refresh its data
      if (detailsOpen && selectedFournisseur) {
        handleOpenDetails(selectedFournisseur);
      }
    };

    const handleFinancialDataUpdate = (e) => {
      setSnackbar({
        open: true,
        message: 'Données financières mises à jour - Métriques actualisées',
        severity: 'info'
      });
      
      // If a supplier details dialog is open, refresh its data
      if (detailsOpen && selectedFournisseur) {
        handleOpenDetails(selectedFournisseur);
      }
    };
    
    const handleReservationDataUpdate = (e) => {
      setSnackbar({
        open: true,
        message: 'Réservations mises à jour - Relations fournisseurs actualisées',
        severity: 'info'
      });
      
      // If a supplier details dialog is open, refresh its data
      if (detailsOpen && selectedFournisseur) {
        handleOpenDetails(selectedFournisseur);
      }
    };
    
    const handleClientDataUpdate = (e) => {
      setSnackbar({
        open: true,
        message: 'Clients mis à jour - Relations fournisseurs actualisées',
        severity: 'info'
      });
      
      // If a supplier details dialog is open, refresh its data
      if (detailsOpen && selectedFournisseur) {
        handleOpenDetails(selectedFournisseur);
      }
    };

    // Add event listeners
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('supplierDataUpdate', handleSupplierEventUpdate);
    window.addEventListener('packageDataUpdate', handlePackageDataUpdate);
    window.addEventListener('financialDataUpdate', handleFinancialDataUpdate);
    window.addEventListener('reservationDataUpdate', handleReservationDataUpdate);
    window.addEventListener('clientDataUpdate', handleClientDataUpdate);

    // Clean up event listeners
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('supplierDataUpdate', handleSupplierEventUpdate);
      window.removeEventListener('packageDataUpdate', handlePackageDataUpdate);
      window.removeEventListener('financialDataUpdate', handleFinancialDataUpdate);
      window.removeEventListener('reservationDataUpdate', handleReservationDataUpdate);
      window.removeEventListener('clientDataUpdate', handleClientDataUpdate);
    };
  }, [
    filterType, filterStatus, filterLocation, searchTerm, 
    suppliersLoading, detailsOpen, selectedFournisseur,
    handleOpenDetails // Add this dependency since we're using it
  ]);

  // Render loading state
  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
          Gestion des Fournisseurs
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Chargement des données des fournisseurs...
        </Typography>
        
        <LinearProgress sx={{ mb: 3 }} />
        
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        
        <Box sx={{ mt: 3 }}>
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
        </Box>
      </Box>
    );
  }
  
  // Render error state
  if (suppliersError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Erreur lors du chargement des données des fournisseurs: {suppliersError}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
        >
          Réessayer
        </Button>
      </Box>
    );
  }

  const handleOpenDialog = (fournisseur = null) => {
    if (fournisseur) {
      setEditingFournisseur(fournisseur);
      setFormData({
        nom: fournisseur.nom,
        type: fournisseur.type,
        pays: fournisseur.pays,
        ville: fournisseur.ville,
        email: fournisseur.email,
        telephone: fournisseur.telephone,
        adresse: fournisseur.adresse,
        personneContact: fournisseur.personneContact,
        statut: fournisseur.statut,
        commission: fournisseur.commission,
        delaiPaiement: fournisseur.delaiPaiement,
        description: fournisseur.description
      });
    } else {
      setEditingFournisseur(null);
      setFormData({
        nom: '',
        type: 'Hôtel',
        pays: '',
        ville: '',
        email: '',
        telephone: '',
        adresse: '',
        personneContact: '',
        statut: 'Actif',
        commission: 0,
        delaiPaiement: 30,
        description: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingFournisseur(null);
  };

  const handleSaveFournisseur = () => {
    try {
      // Get existing suppliers from localStorage
      const existingSuppliers = JSON.parse(localStorage.getItem('suppliersData') || '[]');
      
      let updatedSuppliers;
      let updatedFournisseur;
      
      if (editingFournisseur) {
        // Update existing supplier
        updatedFournisseur = { 
          ...editingFournisseur, 
          ...formData,
          performanceScore: formData.performanceScore || editingFournisseur.performanceScore || Math.floor(Math.random() * 30) + 70,
          satisfactionClient: typeof formData.satisfactionClient === 'number' 
            ? formData.satisfactionClient 
            : typeof editingFournisseur.satisfactionClient === 'number'
              ? editingFournisseur.satisfactionClient
              : parseFloat((Math.random() * 2 + 3).toFixed(1)),
          lastUpdated: new Date().toISOString()
        };
        
        updatedSuppliers = existingSuppliers.map(supplier => 
          supplier.id === editingFournisseur.id ? updatedFournisseur : supplier
        );
      } else {
        // Create new supplier with unique ID
        const newId = `supplier_${Date.now()}`;
        updatedFournisseur = {
          id: newId,
          ...formData,
          performanceScore: Math.floor(Math.random() * 30) + 70,
          satisfactionClient: parseFloat((Math.random() * 2 + 3).toFixed(1)),
          nombreClients: 0,
          nombreServices: Math.floor(Math.random() * 10) + 1,
          chiffreAffaires: 0,
          delaiMoyenLivraison: Math.floor(Math.random() * 5) + 1,
          dateCreation: new Date().toISOString(),
          lastUpdated: new Date().toISOString()
        };
        
        updatedSuppliers = [...existingSuppliers, updatedFournisseur];
      }
      
      // CRITICAL FIX: First close dialog before any state updates that could trigger re-renders
      handleCloseDialog();

      // CRITICAL FIX: Save to localStorage *before* dispatching the event
      localStorage.setItem('suppliersData', JSON.stringify(updatedSuppliers));
      
      // CRITICAL FIX: Set global reference for direct access
      if (typeof window.__travelIT === 'undefined') {
        window.__travelIT = {};
      }
      window.__travelIT.suppliers = updatedSuppliers;
      window.__travelIT.updatedFournisseur = updatedFournisseur;
      
      // CRITICAL FIX: Update the current component state directly
      // This ensures immediate UI updates without waiting for event propagation
      setFilteredFournisseurs(prevFiltered => {
        if (editingFournisseur) {
          // Replace the updated supplier in the array
          return prevFiltered.map(supplier => 
            supplier.id === updatedFournisseur.id ? updatedFournisseur : supplier
          );
        } else {
          // Check if the new supplier matches current filters
          const shouldAdd = 
            (filterType === 'all' || updatedFournisseur.type === filterType) &&
            (filterStatus === 'all' || updatedFournisseur.statut === filterStatus) &&
            (filterLocation === 'all' || updatedFournisseur.pays === filterLocation);
          
          return shouldAdd ? [...prevFiltered, updatedFournisseur] : prevFiltered;
        }
      });
      
      // Show success message
      setSnackbar({
        open: true,
        message: editingFournisseur 
          ? 'Fournisseur modifié avec succès - Données interconnectées mises à jour' 
          : 'Fournisseur ajouté avec succès - Disponible dans tout le système',
        severity: 'success'
      });
      
      // CRITICAL FIX: Dispatch custom event with complete supplier data
      // This ensures other components get the full data set
        const event = new CustomEvent('supplierDataUpdate', { 
          detail: { 
            action: editingFournisseur ? 'update' : 'create',
          supplier: updatedFournisseur,
          allSuppliers: updatedSuppliers
          } 
        });
        window.dispatchEvent(event);
      
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du fournisseur:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la sauvegarde du fournisseur',
        severity: 'error'
      });
    }
  };

  const handleDeleteFournisseur = (fournisseurId) => {
    try {
      if (!fournisseurId) return;
      
      // Get existing suppliers from localStorage
      const existingSuppliers = JSON.parse(localStorage.getItem('suppliersData') || '[]');
      
      // Find the supplier to delete
      const supplierToDelete = existingSuppliers.find(s => s.id === fournisseurId);
      if (!supplierToDelete) {
        setSnackbar({
          open: true,
          message: 'Fournisseur non trouvé',
          severity: 'error'
        });
        return;
      }
      
      // Filter out the supplier to delete
      const updatedSuppliers = existingSuppliers.filter(s => s.id !== fournisseurId);
      
      // CRITICAL FIX: Clear UI state first
      setAnchorEl(null);
      setSelectedFournisseur(null);
      
      // CRITICAL FIX: Update global reference
      if (typeof window.__travelIT === 'undefined') {
        window.__travelIT = {};
      }
      window.__travelIT.suppliers = updatedSuppliers;
      window.__travelIT.deletedSupplierId = fournisseurId;
      
      // CRITICAL FIX: Update component state directly for immediate UI feedback
      setFilteredFournisseurs(prevFiltered => 
        prevFiltered.filter(supplier => supplier.id !== fournisseurId)
      );
      
      // CRITICAL FIX: Save to localStorage *before* dispatching the event
      localStorage.setItem('suppliersData', JSON.stringify(updatedSuppliers));
      
      // Update related data (packages, transactions)
      // 1. Get and update packages
      const existingPackages = JSON.parse(localStorage.getItem('packagesData') || '[]');
      const updatedPackages = existingPackages.map(pkg => {
        if (pkg.fournisseurId === fournisseurId) {
          // Remove supplier reference or mark as unavailable
          return { ...pkg, fournisseurId: null, statut: 'Indisponible' };
        }
        return pkg;
      });
      localStorage.setItem('packagesData', JSON.stringify(updatedPackages));
      
      // 2. Get and update transactions
      const existingTransactions = JSON.parse(localStorage.getItem('transactionsData') || '[]');
      const updatedTransactions = existingTransactions.map(transaction => {
        if (transaction.fournisseurId === fournisseurId) {
          // Remove supplier reference
          return { ...transaction, fournisseurId: null };
        }
        return transaction;
      });
      localStorage.setItem('transactionsData', JSON.stringify(updatedTransactions));
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Fournisseur supprimé - Forfaits et finances mises à jour',
        severity: 'warning'
      });
      
      // CRITICAL FIX: Dispatch events with complete data after all updates are done
      // Ensure all components get notified properly
      
      // 1. Supplier event
      const supplierEvent = new CustomEvent('supplierDataUpdate', { 
        detail: { 
          action: 'delete', 
          supplierId: fournisseurId,
          allSuppliers: updatedSuppliers
        } 
      });
      window.dispatchEvent(supplierEvent);
      
      // 2. Package event
      const packageEvent = new CustomEvent('packageDataUpdate', { 
        detail: { 
          action: 'supplierDeleted', 
          supplierId: fournisseurId,
          allPackages: updatedPackages
        } 
      });
      window.dispatchEvent(packageEvent);
      
      // 3. Financial event
      const financialEvent = new CustomEvent('financialDataUpdate', { 
        detail: { 
          action: 'supplierDeleted', 
          supplierId: fournisseurId,
          allTransactions: updatedTransactions
        } 
      });
      window.dispatchEvent(financialEvent);
      
    } catch (error) {
      console.error('Erreur lors de la suppression du fournisseur:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la suppression du fournisseur',
        severity: 'error'
      });
    }
  };

  const handleMenuClick = (event, fournisseur) => {
    setAnchorEl(event.currentTarget);
    setSelectedFournisseur(fournisseur);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFournisseur(null);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Compagnie aérienne': return <FlightIcon />;
      case 'Hôtel': return <HotelIcon />;
      case 'Transport': return <BusIcon />;
      case 'Restaurant': return <RestaurantIcon />;
      case 'Assurance': return <InsuranceIcon />;
      case 'Tour opérateur': return <TourIcon />;
      default: return <BusinessIcon />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Compagnie aérienne': return 'primary';
      case 'Hôtel': return 'success';
      case 'Transport': return 'info';
      case 'Restaurant': return 'warning';
      case 'Assurance': return 'error';
      case 'Tour opérateur': return 'secondary';
      default: return 'default';
    }
  };

  const getStatusColor = (statut) => {
    switch (statut) {
      case 'Actif': return 'success';
      case 'En pause': return 'warning';
      case 'Inactif': return 'error';
      default: return 'default';
    }
  };

  // Function to format currency correctly
  const formatCurrencyForPDF = (amount) => {
    // Convert number to string with spaces as thousand separators
    const numberPart = Math.round(amount || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return `${numberPart} DZD`;
  };

  const exportFournisseursToPDF = () => {
    try {
      // Create new PDF document
      const doc = new jsPDF('landscape');
      
      // Document dimensions
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      
      // Colors
      const primaryColor = [30, 64, 175]; // blue
      const secondaryColor = [124, 58, 237]; // purple
      const successColor = [16, 185, 129]; // green
      const warningColor = [245, 158, 11]; // amber
      const textColor = [31, 41, 55]; // dark gray
      
      // Add header
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, pageWidth, 25, 'F');
      
      // Add agency logo and name
      if (settings.agencyLogo) {
        try {
          // Add the agency logo from base64 string
          doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
          
          // Add agency name
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
        } catch (logoError) {
          console.error('Error adding logo to PDF:', logoError);
          // Fallback to text only
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
        }
      } else {
        // No logo, just add agency name
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
      }
      
      // Add title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text("Rapport des Fournisseurs", pageWidth - margin, 17, { align: 'right' });
      
      // Add date and filters
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      doc.setFontSize(10);
      doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, margin, 35);
      doc.text(`Filtres: Type: ${filterType === 'all' ? 'Tous' : filterType} | Statut: ${filterStatus === 'all' ? 'Tous' : filterStatus} | Région: ${filterLocation === 'all' ? 'Toutes' : filterLocation}`, margin, 42);
      
      // Add summary section
      doc.setDrawColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.setLineWidth(0.5);
      doc.line(margin, 48, pageWidth - margin, 48);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Résumé des Fournisseurs", margin, 58);
      
      // Calculate some key metrics
      const totalFournisseurs = filteredFournisseurs.length;
      const activeFournisseurs = filteredFournisseurs.filter(f => f.statut === 'Actif').length;
      const activePercentage = totalFournisseurs > 0 ? Math.round((activeFournisseurs / totalFournisseurs) * 100) : 0;
      
      // Get types and their counts
      const typeCounts = {};
      filteredFournisseurs.forEach(f => {
        if (!typeCounts[f.type]) {
          typeCounts[f.type] = 0;
        }
        typeCounts[f.type]++;
      });
      
      // Get countries and their counts
      const countryCounts = {};
      filteredFournisseurs.forEach(f => {
        if (f.pays && !countryCounts[f.pays]) {
          countryCounts[f.pays] = 0;
        }
        if (f.pays) {
          countryCounts[f.pays]++;
        }
      });
      
      // Summary stats
      const stats = [
        { label: "Total des Fournisseurs", value: `${totalFournisseurs}` },
        { label: "Fournisseurs Actifs", value: `${activeFournisseurs} (${activePercentage}%)` },
        { label: "Chiffre d'Affaires", value: formatCurrencyForPDF(totalChiffreAffaires) },
        { label: "Performance Moyenne", value: `${performanceMoyenne.toFixed(1)}%` },
        { label: "Note Moyenne", value: `${noteMoyenne.toFixed(1)}/5` }
      ];
      
      // Add KPI table
      doc.autoTable({
        startY: 62,
        head: [['Indicateur', 'Valeur']],
        body: stats.map(item => [item.label, item.value]),
        theme: 'grid',
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] }
      });
      
      // Add type breakdown
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Répartition par Type", margin, doc.lastAutoTable.finalY + 15);
      
      const typeStats = Object.entries(typeCounts)
        .map(([type, count]) => ({
          type,
          count,
          percentage: (count / totalFournisseurs * 100).toFixed(1)
        }))
        .sort((a, b) => b.count - a.count);
      
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 20,
        head: [['Type de Fournisseur', 'Nombre', 'Pourcentage']],
        body: typeStats.map(item => [
          item.type, 
          item.count.toString(),
          `${item.percentage}%`
        ]),
        theme: 'grid',
        headStyles: { fillColor: secondaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] }
      });
      
      // Add location breakdown
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Répartition par Pays", margin, doc.lastAutoTable.finalY + 15);
      
      const countryStats = Object.entries(countryCounts)
        .map(([country, count]) => ({
          country,
          count,
          percentage: (count / totalFournisseurs * 100).toFixed(1)
        }))
        .sort((a, b) => b.count - a.count);
      
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 20,
        head: [['Pays', 'Nombre de Fournisseurs', 'Pourcentage']],
        body: countryStats.map(item => [
          item.country, 
          item.count.toString(),
          `${item.percentage}%`
        ]),
        theme: 'grid',
        headStyles: { fillColor: successColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] }
      });
      
      // Add supplier details
      doc.addPage();
      
      // Add header on new page
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, pageWidth, 25, 'F');
      
      // Add agency logo and name
      if (settings.agencyLogo) {
        try {
          // Add the agency logo from base64 string
          doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
          
          // Add agency name
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
        } catch (logoError) {
          console.error('Error adding logo to PDF:', logoError);
          // Fallback to text only
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
        }
      } else {
        // No logo, just add agency name
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
      }
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.text("Liste des Fournisseurs", pageWidth - margin, 17, { align: 'right' });
      
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      doc.setFontSize(12);
      doc.text("Détails de tous les fournisseurs", margin, 40);
      
      // Add suppliers table
      const suppliersTableData = filteredFournisseurs.map(f => [
        f.id,
        f.nom,
        f.type,
        `${f.ville}, ${f.pays}`,
        f.personneContact,
        f.email,
        f.telephone,
        f.statut,
        f.commission ? `${f.commission}%` : 'N/A'
      ]);
      
      doc.autoTable({
        startY: 50,
        head: [['ID', 'Nom', 'Type', 'Localisation', 'Contact', 'Email', 'Téléphone', 'Statut', 'Commission']],
        body: suppliersTableData,
        theme: 'grid',
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 15 },
          1: { cellWidth: 40 },
          2: { cellWidth: 25 },
          3: { cellWidth: 35 },
          4: { cellWidth: 30 },
          5: { cellWidth: 40 },
          6: { cellWidth: 25 },
          7: { cellWidth: 20 },
          8: { cellWidth: 22 }
        },
        margin: { left: margin, right: margin },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        didDrawPage: function (data) {
          // Add header on each page
          doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
          doc.rect(0, 0, pageWidth, 25, 'F');
          
          doc.setFillColor(255, 255, 255);
          doc.roundedRect(margin, 10, 30, 10, 1, 1, 'F');
          doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          // Add agency logo and name
        if (settings.agencyLogo) {
          try {
            // Add the agency logo from base64 string
            doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
            
            // Add agency name
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
          } catch (logoError) {
            console.error('Error adding logo to PDF:', logoError);
            // Fallback to text only
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
          }
        } else {
          // No logo, just add agency name
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
        }
          
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(16);
          doc.text("Liste des Fournisseurs", pageWidth - margin, 17, { align: 'right' });
          
          // Add footer
          const pageNumber = doc.internal.getNumberOfPages();
          doc.setFontSize(10);
          doc.setTextColor(100, 100, 100);
          doc.text(`Page ${pageNumber}`, margin, pageHeight - 10);
          doc.text(`${settings.agencyName || 'Agence de Voyage'} | Page ${pageNumber} sur ${doc.internal.getNumberOfPages()}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
          doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
        }
      });
      
      // Save the PDF with agency name
      const dateStr = new Date().toISOString().split('T')[0];
      const fileName = `${settings.agencyName || 'Agence'}_Rapport_Fournisseurs_${dateStr}.pdf`;
      doc.save(fileName);
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Export PDF réussi',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de l\'export PDF',
        severity: 'error'
      });
    }
  };

  // Count interconnected data
  const connectedPackages = interconnectedPackages.length;
  const connectedTransactions = interconnectedTransactions.length;
  const connectedCountries = uniqueCountries.length;
  const connectedReservations = interconnectedReservations.length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Gestion des Fournisseurs
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>

          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ height: 40 }}
          >
            Nouveau Fournisseur
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PictureAsPdfIcon />}
            onClick={exportFournisseursToPDF}
            sx={{ height: 40 }}
          >
            Exporter PDF
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ width: '100%', mt: 4 }}>
          <LinearProgress />
          <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
            Chargement des données fournisseurs interconnectées...
          </Typography>
        </Box>
      ) : (
        <>
      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card 
              sx={{ 
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(20px)',
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      {stat.isCurrency ? formatCurrency(stat.value) : stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {stat.subtitle}
                    </Typography>
                    <Chip 
                      label={stat.trend} 
                      size="small" 
                      color={stat.color}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  <Avatar 
                    sx={{ 
                      bgcolor: `${stat.color}.main`,
                      width: 56,
                      height: 56
                    }}
                  >
                    <stat.icon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
              borderRadius: 3,
              border: '1px solid rgba(255, 255, 255, 0.2)',
              height: 280
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                📊 Performance des Fournisseurs
              </Typography>
                  <SupplierPerformanceChart data={performanceChartData} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
              borderRadius: 3,
              border: '1px solid rgba(255, 255, 255, 0.2)',
              height: 280
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                📈 Distribution par Type
              </Typography>
                  <SupplierDistributionChart data={typeDistribution} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {/* Top Performers Section - Removed as requested */}
          {/* <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card 
                sx={{ 
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: 3,
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    🏆 Meilleurs Fournisseurs
                  </Typography>
                  <List>
                    {supplierMetrics.topPerformers.map((supplier, index) => (
                      <ListItem key={index} divider={index < supplierMetrics.topPerformers.length - 1}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'success.main' }}>
                            {getTypeIcon(supplier.type)}
                          </Avatar>
                        </ListItemAvatar>
                        <MuiListItemText 
                          primary={supplier.nom} 
                          secondary={`Score: ${supplier.performanceScore || 0}% | ${supplier.type}`}
                        />
                        <Box>
                          <Rating value={supplier.rating || 4.5} readOnly size="small" />
                          <Typography variant="caption" color="text.secondary" display="block">
                            {formatCurrency(supplierMetrics.revenueBySupplier[supplier.id] || 0)}
                          </Typography>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card 
                sx={{ 
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: 3,
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    ⚠️ Fournisseurs à Surveiller
                  </Typography>
                  <List>
                    {supplierMetrics.lowPerformers.map((supplier, index) => (
                      <ListItem key={index} divider={index < supplierMetrics.lowPerformers.length - 1}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'error.main' }}>
                            {getTypeIcon(supplier.type)}
                          </Avatar>
                        </ListItemAvatar>
                        <MuiListItemText 
                          primary={supplier.nom} 
                          secondary={`Score: ${supplier.performanceScore || 0}% | ${supplier.type}`}
                        />
                        <Box>
                          <Rating value={supplier.rating || 2.5} readOnly size="small" />
                          <Typography variant="caption" color="text.secondary" display="block">
                            {formatCurrency(supplierMetrics.revenueBySupplier[supplier.id] || 0)}
                          </Typography>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid> */}

      {/* Search and Filters */}
      <Card 
        sx={{ 
          mb: 3,
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          borderRadius: 3,
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}
      >
        <CardContent>
              <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Rechercher un fournisseur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={filterType}
                  label="Type"
                  onChange={(e) => setFilterType(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">Tous</MenuItem>
                      {uniqueTypes.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Statut</InputLabel>
                <Select
                  value={filterStatus}
                  label="Statut"
                  onChange={(e) => setFilterStatus(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">Tous</MenuItem>
                  <MenuItem value="Actif">Actifs</MenuItem>
                  <MenuItem value="Suspendu">Suspendus</MenuItem>
                  <MenuItem value="Inactif">Inactifs</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Pays</InputLabel>
                <Select
                  value={filterLocation}
                  label="Pays"
                  onChange={(e) => setFilterLocation(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">Tous</MenuItem>
                      {uniqueCountries.map((pays) => (
                        <MenuItem key={pays} value={pays}>{pays}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, newMode) => newMode && setViewMode(newMode)}
                  size="small"
                      sx={{ mr: 1 }}
                >
                      <ToggleButton value="cards" aria-label="cards">
                        <ViewModuleIcon fontSize="small" />
                  </ToggleButton>
                      <ToggleButton value="list" aria-label="list">
                        <ViewListIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
                <Button
                  variant="outlined"
                  startIcon={<ExportIcon />}
                  onClick={exportFournisseursToPDF}
                  sx={{ 
                    borderRadius: 2,
                    mr: 1
                  }}
                >
                  Exporter
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                  sx={{ 
                    borderRadius: 2,
                        background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
                        flex: 1
                  }}
                >
                  Nouveau
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Enhanced Suppliers Display */}
      {viewMode === 'cards' ? (
        <Grid container spacing={3}>
          {filteredFournisseurs
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((fournisseur) => (
              <Grid item xs={12} md={6} lg={4} key={fournisseur.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: 3,
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                          boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
                          cursor: 'pointer'
                    }
                  }}
                      onClick={() => handleOpenDetails(fournisseur)}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar 
                        src={fournisseur.avatar}
                        sx={{ width: 48, height: 48 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {fournisseur.nom}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getTypeIcon(fournisseur.type)}
                          <Typography variant="body2" color="text.secondary">
                            {fournisseur.type}
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton 
                        onClick={(e) => handleMenuClick(e, fournisseur)}
                        size="small"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>

                    {/* Location and Contact */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {fournisseur.ville}, {fournisseur.pays}
                        </Typography>
                        <Chip
                          label={fournisseur.statut}
                          color={getStatusColor(fournisseur.statut)}
                          size="small"
                          sx={{ ml: 'auto' }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {fournisseur.personneContact}
                        </Typography>
                      </Box>
                    </Box>

                    {/* No performance metrics - only showing user-entered data */}

                    {/* Commission - user-entered data */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                          {fournisseur.commission}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Commission
                        </Typography>
                      </Box>
                    </Box>

                    {/* Interconnected Data */}
                    {fournisseur.forfaitsGeres?.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="success.main" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                          📦 Forfaits Gérés
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {fournisseur.forfaitsGeres.slice(0, 2).map((forfait, index) => (
                            <Chip
                              key={index}
                              label={forfait.nom}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          ))}
                          {fournisseur.forfaitsGeres.length > 2 && (
                            <Chip
                              label={`+${fournisseur.forfaitsGeres.length - 2}`}
                              size="small"
                              color="default"
                            />
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Financial Summary */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Box sx={{ flex: 1, textAlign: 'center', p: 1, bgcolor: 'rgba(237, 108, 2, 0.04)', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Paiement
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {fournisseur.delaiPaiement}j
                        </Typography>
                      </Box>
                    </Box>

                    {/* Add Modifier and Supprimer buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Button 
                        size="small" 
                        startIcon={<EditIcon />}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent card click
                          handleOpenDialog(fournisseur);
                        }}
                      >
                        Modifier
                      </Button>
                      <Button 
                        size="small" 
                        color="error" 
                        startIcon={<DeleteIcon />}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent card click
                          handleDeleteFournisseur(fournisseur.id);
                        }}
                      >
                        Supprimer
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
      ) : (
        <Card 
          sx={{ 
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Fournisseur</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Localisation</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Commission</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Délai Paiement</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredFournisseurs
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((fournisseur) => (
                    <TableRow key={fournisseur.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar src={fournisseur.avatar} sx={{ width: 40, height: 40 }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {fournisseur.nom}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {fournisseur.personneContact}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getTypeIcon(fournisseur.type)}
                          label={fournisseur.type}
                          color={getTypeColor(fournisseur.type)}
                          size="small"
                          sx={{ borderRadius: 2 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {fournisseur.ville}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {fournisseur.pays}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                          {fournisseur.commission}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {fournisseur.delaiPaiement} jours
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={fournisseur.statut}
                          color={getStatusColor(fournisseur.statut)}
                          size="small"
                          sx={{ borderRadius: 2 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button 
                            size="small" 
                            startIcon={<EditIcon />}
                            onClick={() => handleOpenDialog(fournisseur)}
                          >
                            Modifier
                          </Button>
                          <Button 
                            size="small" 
                            color="error" 
                            startIcon={<DeleteIcon />}
                            onClick={() => handleDeleteFournisseur(fournisseur.id)}
                          >
                            Supprimer
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <TablePagination
          rowsPerPageOptions={[6, 12, 24]}
          component="div"
          count={filteredFournisseurs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Lignes par page:"
        />
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }
        }}
      >
        <MenuListItem onClick={() => { handleOpenDialog(selectedFournisseur); handleMenuClose(); }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Modifier</ListItemText>
        </MenuListItem>
            <MenuListItem onClick={() => { handleOpenDetails(selectedFournisseur); handleMenuClose(); }}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Voir détails</ListItemText>
        </MenuListItem>
        <MenuListItem>
          <ListItemIcon>
            <AssessmentIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Rapport performance</ListItemText>
        </MenuListItem>
        <MenuListItem>
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Imprimer fiche</ListItemText>
        </MenuListItem>
        <Divider />
        <MenuListItem onClick={() => { handleDeleteFournisseur(selectedFournisseur?.id); handleMenuClose(); }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Supprimer</ListItemText>
        </MenuListItem>
      </Menu>

      {/* Enhanced Add/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {editingFournisseur ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Les données seront synchronisées avec les forfaits et finances
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom du fournisseur"
                value={formData.nom}
                onChange={(e) => setFormData({...formData, nom: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type de service</InputLabel>
                <Select
                  value={formData.type}
                  label="Type de service"
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="Hôtel">🏨 Hôtel</MenuItem>
                  <MenuItem value="Compagnie aérienne">✈️ Compagnie aérienne</MenuItem>
                  <MenuItem value="Transport">🚌 Transport</MenuItem>
                  <MenuItem value="Restaurant">🍽️ Restaurant</MenuItem>
                  <MenuItem value="Assurance">🛡️ Assurance</MenuItem>
                  <MenuItem value="Tour opérateur">🗺️ Tour opérateur</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Pays"
                value={formData.pays}
                onChange={(e) => setFormData({...formData, pays: e.target.value})}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PublicIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ville"
                value={formData.ville}
                onChange={(e) => setFormData({...formData, ville: e.target.value})}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Téléphone"
                value={formData.telephone}
                onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Adresse complète"
                value={formData.adresse}
                onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Personne de contact"
                value={formData.personneContact}
                onChange={(e) => setFormData({...formData, personneContact: e.target.value})}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Statut du partenariat</InputLabel>
                <Select
                  value={formData.statut}
                  label="Statut du partenariat"
                  onChange={(e) => setFormData({...formData, statut: e.target.value})}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="Actif">✅ Actif</MenuItem>
                  <MenuItem value="Suspendu">⏸️ Suspendu</MenuItem>
                  <MenuItem value="Inactif">❌ Inactif</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Financial Terms */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                💰 Conditions Financières
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.04)', borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Commission (%)"
                      type="number"
                      value={formData.commission}
                      onChange={(e) => setFormData({...formData, commission: parseFloat(e.target.value)})}
                      inputProps={{ min: 0, max: 100, step: 0.5 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MoneyIcon />
                          </InputAdornment>
                        ),
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Délai de paiement (jours)"
                      type="number"
                      value={formData.delaiPaiement}
                      onChange={(e) => setFormData({...formData, delaiPaiement: parseInt(e.target.value)})}
                      inputProps={{ min: 0, max: 365 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ScheduleIcon />
                          </InputAdornment>
                        ),
                        endAdornment: <InputAdornment position="end">jours</InputAdornment>
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description des services"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Décrivez les services proposés par ce fournisseur..."
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            {/* Interconnection Info */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'rgba(76, 175, 80, 0.04)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'success.main' }}>
                  🔗 Interconnexions Automatiques
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label="Synchronisation Forfaits" size="small" color="primary" />
                  <Chip label="Suivi Financier" size="small" color="secondary" />
                  <Chip label="Métriques Performance" size="small" color="success" />
                  <Chip label="Analytics Temps Réel" size="small" color="warning" />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleCloseDialog} sx={{ borderRadius: 2 }}>
            Annuler
          </Button>
          <Button 
            onClick={handleSaveFournisseur}
            variant="contained"
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)'
            }}
          >
            {editingFournisseur ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

          {/* Supplier Details Dialog */}
          <Dialog
            open={detailsOpen}
            onClose={handleCloseDetails}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)'
              }
            }}
          >
            <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Détails du Fournisseur
              </Typography>
              <IconButton onClick={handleCloseDetails}>
                <CancelIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <SupplierDetails supplier={selectedFournisseur} />
            </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({...snackbar, open: false})}
      >
        <Alert 
          onClose={() => setSnackbar({...snackbar, open: false})} 
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
        </>
      )}
    </Box>
  );
};

export default Fournisseurs; 